import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LottieAnimation from './LottieAnimation';

interface AnimatedLoadingTextProps {
  isLoading: boolean;
  title?: string;
  text?: string | React.ReactNode;
  loadingText?: string;
  numLines?: number;
  showAnimation?: boolean;
  lottieSrc?: string;
  className?: string;
}

const AnimatedLoadingText: React.FC<AnimatedLoadingTextProps> = ({
  isLoading,
  title,
  text,
  numLines = 4,
  showAnimation = true,
  lottieSrc = '/loading-lottie-A.json',
  className = '',
  loadingText = 'Cargando...',
}) => {
  const skeletonLines = useMemo(() => {
    const widths = ['w-11/12', 'w-10/12', 'w-9/12', 'w-8/12', 'w-6/12'];
    return Array.from({ length: Math.max(1, numLines) }).map((_, idx) => widths[idx % widths.length]);
  }, [numLines]);

  return (
    <div className={`glass-surface ${className}`}>
      {title && (
        <div className="mb-2">
          <h3 className="text-lg font-medium text-text-primary">{title}</h3>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <AnimatePresence initial={false} mode="wait">
          {isLoading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col gap-4"
            >
              {showAnimation && (
                <div className="flex items-center gap-3">
                  <LottieAnimation path={lottieSrc} width="40px" height="40px" />
                  <p className="text-sm text-text-secondary">{loadingText}</p>
                </div>
              )}

              <div className="flex flex-col gap-2 animate-pulse">
                {skeletonLines.map((widthClass, i) => (
                  <div key={i} className={`h-3 rounded ${widthClass} bg-neutral-200 dark:bg-neutral-700`} />
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
              className="text-text-primary whitespace-pre-wrap leading-relaxed text-sm"
            >
              {text}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AnimatedLoadingText;
